import React, { useState, useEffect } from 'react';
import { Code, Save, Loader2, Shield, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmbedSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    embed_rate_limit: 60,
  });

  const getAuthHeader = () => {
    const token = localStorage.getItem('adminToken');
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/embed-settings`, {
        headers: getAuthHeader()
      });
      if (response.data) {
        setSettings(response.data);
      }
    } catch (error) {
      console.error('Error fetching embed settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (settings.embed_rate_limit < 1 || settings.embed_rate_limit > 1000) {
      toast.error('Rate limit must be between 1 and 1000 requests/minute');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/admin/embed-settings`, settings, {
        headers: getAuthHeader()
      });
      toast.success('Embed settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="embed-settings-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Code className="w-7 h-7 text-amber-400" />
            Embed Settings
          </h1>
          <p className="text-slate-400 mt-1">Configure iframe embedding settings for partners</p>
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-500/10 border-blue-500/20">
        <CardContent className="p-6">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-300">
              <p className="font-medium mb-2">About Partner Iframe Embedding</p>
              <ul className="list-disc list-inside space-y-1 text-blue-300/80">
                <li>Partners can embed the client portal on their own websites using an iframe</li>
                <li>Each partner configures their allowed embed domain in their settings</li>
                <li>The system enforces domain validation using CSP headers (browser-level security)</li>
                <li>Rate limiting prevents abuse - each partner is limited by requests/minute</li>
                <li>Only clients belonging to the partner can access the embedded portal</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Rate Limit Card */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            Rate Limiting
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-slate-800/50 rounded-xl p-4">
            <p className="text-slate-400 text-sm mb-4">
              Set the maximum number of API requests allowed per minute for each partner&apos;s embedded portal.
              This helps prevent abuse while ensuring legitimate users can access the system.
            </p>
          </div>

          <div className="grid gap-6 max-w-md">
            <div className="space-y-2">
              <Label htmlFor="rateLimit" className="text-slate-300 font-medium">
                Requests per Minute (per Partner)
              </Label>
              <div className="flex items-center gap-4">
                <Input
                  id="rateLimit"
                  type="number"
                  min="1"
                  max="1000"
                  value={settings.embed_rate_limit}
                  onChange={(e) => setSettings({
                    ...settings,
                    embed_rate_limit: parseInt(e.target.value) || 60
                  })}
                  className="bg-slate-800 border-slate-700 text-white w-32"
                  data-testid="rate-limit-input"
                />
                <span className="text-slate-400">req/min</span>
              </div>
              <p className="text-slate-500 text-sm">
                Recommended: 60 for normal usage, 100+ for high-traffic partners
              </p>
            </div>

            {/* Quick presets */}
            <div className="space-y-2">
              <Label className="text-slate-400 text-sm">Quick presets:</Label>
              <div className="flex flex-wrap gap-2">
                {[
                  { label: 'Low (30)', value: 30 },
                  { label: 'Standard (60)', value: 60 },
                  { label: 'High (100)', value: 100 },
                  { label: 'Very High (200)', value: 200 },
                ].map((preset) => (
                  <Button
                    key={preset.value}
                    variant="outline"
                    size="sm"
                    onClick={() => setSettings({
                      ...settings,
                      embed_rate_limit: preset.value
                    })}
                    className={`border-slate-700 ${
                      settings.embed_rate_limit === preset.value
                        ? 'bg-amber-500/20 border-amber-500/50 text-amber-400'
                        : 'text-slate-400 hover:text-white hover:bg-slate-800'
                    }`}
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="pt-4 border-t border-slate-800">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
              data-testid="save-embed-settings-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Settings
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmbedSettings;
